import {
  ADMINISTRATION_KINDS,
  ADMINISTRATION_ROLES,
} from '../../../constants/administration-kinds';
import {
  ActuationDocument,
  ActuationDocumentStructure,
  ActuationStudy,
} from '../../../types';
import {
  DURATION_TERM_TYPES,
  DURATION_TYPE,
  DURATION_TYPES,
} from '../../../constants/durations';
import { RES_FACULTY_DISPLAY } from '../types';
import { STUDY_ACTUATION_KINDS } from '../../../constants/actuation-kinds';

type StudyHistoryTableCell = {
  title: string;
  subtitle?: string;
  content: StudyHistoryTableCellContent;
  hasChanged?: boolean;
};

export type StudyHistoryTableCellContent = string | Array<string>;
type StudyHistoryTableRow = StudyHistoryTableCell[];

export const buildDurationContent = (study: ActuationStudy) => {
  const duration =
    DURATION_TYPES.find(
      (duration) => duration.value === study.duration.data.durationType,
    )?.displayName || '';

  if (study.duration.data.durationType === DURATION_TYPE.RENEWABLE) {
    const { term, termType } = study.duration.data;
    const durationTermTypeDisplay =
      DURATION_TERM_TYPES.find((type) => type.value === termType)
        ?.displayName || '';
    return {
      content: `${duration} ${term} ${durationTermTypeDisplay}`,
    };
  }
  if (study.duration.data.durationType === DURATION_TYPE.DEFINITE) {
    return { content: `${duration} ${study.duration.data.endDate}` };
  }

  return { content: duration };
};

export class StudyHistoryTable {
  public headerRow: StudyHistoryTableRow = [];
  public bodyRows: StudyHistoryTableRow[] = [];

  constructor(documents: ActuationDocument[]) {
    this.headerRow = this.createHeaderRows(documents);
    this.bodyRows = this.createBodyRows(documents);
  }

  createHeaderRows(documents: ActuationDocument[]) {
    return documents.map(({ document }) => ({
      title:
        STUDY_ACTUATION_KINDS.find(
          (kind) => kind.value === document.documentType,
        )?.displayName || '',
      content: document.creationDate,
    }));
  }

  createBodyRows(documents: ActuationDocument[]) {
    const bussinesNameRow = this.createRow(
      documents,
      'Razón Social',
      (study: ActuationStudy) => {
        return { content: study.businessName.data.businessName };
      },
    );

    const addressRow = this.createRow(
      documents,
      'Domicilio',
      (study: ActuationStudy) => {
        return { content: study.address.data.address };
      },
    );

    const durationRow = this.createRow(
      documents,
      'Duración',
      buildDurationContent,
    );

    const objectRow = this.createRow(
      documents,
      'Objeto',
      (study: ActuationStudy) => {
        return { content: study.corporatePurpose.data.corporatePurpose };
      },
    );

    const shareHolderRow = this.createRow(
      documents,
      'Accionistas',
      (study: ActuationStudy) => {
        const shareHolderSummaryText = study.shareholders.data.shareholders.map(
          ({ name, rut, money }) => {
            return `${name}, ${rut} con $${money.toLocaleString('es-CL')}`;
          },
        );

        return {
          subtitle: `(${study.shareholders.data.shareholders.length} Accionista/s)`,
          content: shareHolderSummaryText,
        };
      },
    );

    const administrationRow = this.createRow(
      documents,
      'Administración',
      (study: ActuationStudy) => {
        return {
          content:
            ADMINISTRATION_KINDS.find(
              (kind) =>
                kind.value === study.administration.data.administrationType,
            )?.displayName || '',
        };
      },
    );

    const administratorsRow = this.createRow(
      documents,
      `Administradores`,
      (study: ActuationStudy) => {
        const adminSummaryText = study.administration.data.administrators.map(
          ({ name, rut, role, transitory }) => {
            return [
              name,
              rut,
              ADMINISTRATION_ROLES.find((r) => r.value === role)?.displayName ||
                '',
              transitory && 'provisorio',
            ]
              .filter(Boolean)
              .join(', ');
          },
        );

        return {
          subtitle: `(${study.administration.data.administrators.length} Administrador/es)`,
          content: adminSummaryText,
        };
      },
    );

    const facultiesRow = this.createRow(
      documents,
      'Facultades',
      (study: ActuationStudy) => {
        const facultiesList = study.faculties.data.faculties.map(
          (faculty) => RES_FACULTY_DISPLAY[faculty],
        );

        if (!facultiesList.length) {
          return { content: 'Sin facultades conocidas' };
        }

        return {
          subtitle: `(${facultiesList.length} Facultad/es)`,
          content: facultiesList,
        };
      },
    );

    const exhibitsRow = this.createRow(
      documents,
      'Anexos',
      (documentStructure: ActuationDocumentStructure) => {
        const exhibitsTexts = documentStructure.anexos?.map(
          (exhibit) => exhibit.texto,
        );

        if (!exhibitsTexts?.length) {
          return { content: 'Sin anexos' };
        }

        return {
          subtitle: `(${exhibitsTexts.length} Anexo/s)`,
          content: exhibitsTexts,
        };
      },
    );

    return [
      bussinesNameRow,
      addressRow,
      durationRow,
      objectRow,
      shareHolderRow,
      administrationRow,
      administratorsRow,
      facultiesRow,
      exhibitsRow,
    ];
  }

  createRow(
    documents: ActuationDocument[],
    title: string,
    getCellText: (input: ActuationStudy | ActuationDocumentStructure) => {
      subtitle?: string;
      content: StudyHistoryTableCellContent;
    },
  ): StudyHistoryTableRow {
    const historyTableRow = documents.reduce((row, document, i) => {
      const studyAndStructure = {
        ...document.study,
        ...document.documentStructure,
      };
      const { subtitle, content } = getCellText(studyAndStructure);
      const hasChanged =
        i > 0 && JSON.stringify(content) !== JSON.stringify(row[i - 1].content);

      row.push({
        title,
        subtitle,
        content,
        hasChanged,
      });

      return row;
    }, []);

    return historyTableRow;
  }
}
